import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Phone } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Breadcrumb } from '../components/Breadcrumb';
import { BreadcrumbSchema } from '../components/SchemaOrg';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const ZoneIntervention = () => {
  const zones = [
    {
      name: 'Paris 17e (75017)',
      description: 'Notre siège social est situé au 18 rue Legendre dans le 17e arrondissement, ce qui nous permet d\'intervenir en moins de 15 minutes dans tout l\'arrondissement. Nos plombiers connaissent parfaitement les quartiers des Batignolles, des Ternes, de la Plaine Monceau et des Épinettes, du bâti haussmannien aux immeubles plus récents. Disponibles 24h/24 pour toute urgence.',
      delai: 'Moins de 15 minutes'
    },
    {
      name: 'Paris 8e (75008)',
      description: 'Arrondissement limitrophe du 17e, le 8e (quartiers Monceau, Champs-Élysées, Madeleine) bénéficie d\'une proximité immédiate avec notre base. Nous intervenons rapidement pour tous vos besoins en plomberie : dépannage urgent, installation sanitaire, rénovation complète dans les appartements de standing et immeubles haussmanniens.',
      delai: '15-20 minutes'
    },
    {
      name: 'Paris 9e (75009)',
      description: 'Le 9e arrondissement (Opéra, Pigalle, Trinité) est couvert par nos services de plomberie professionnels. Que ce soit dans un immeuble ancien ou une copropriété récente, nous intervenons pour tous types de travaux : réparation de fuites, débouchage de canalisations, installation de sanitaires, mise en conformité.',
      delai: '15-20 minutes'
    },
    {
      name: 'Paris 16e (75016)',
      description: 'Les habitants du 16e arrondissement (Auteuil, Passy, Trocadéro) peuvent compter sur NHM Plomberie pour tous leurs besoins en plomberie et chauffage. Nous sommes spécialisés dans les interventions au sein d\'appartements haut de gamme et d\'immeubles haussmanniens, avec un souci constant de qualité et de propreté.',
      delai: '20-25 minutes'
    },
    {
      name: 'Paris 18e (75018)',
      description: 'NHM Plomberie dessert le 18e arrondissement (Montmartre, La Chapelle, Goutte d\'Or) pour tous travaux de plomberie, du simple dépannage à la rénovation complète. Nos plombiers sont formés aux spécificités des immeubles anciens comme des constructions modernes. Service d\'urgence disponible jour et nuit.',
      delai: '15-20 minutes'
    },
    {
      name: 'Levallois-Perret (92300)',
      description: 'Commune limitrophe du 17e arrondissement, Levallois-Perret est dans notre zone d\'intervention prioritaire. Nous assurons tous types de prestations en plomberie : recherche de fuite non-destructive, débouchage haute pression, installation de chauffe-eau thermodynamiques, rénovation de salle de bain.',
      delai: '15-20 minutes'
    },
    {
      name: 'Clichy (92110)',
      description: 'Les résidents de Clichy bénéficient de nos services de plomberie professionnels. Proche de notre base, nous intervenons rapidement pour tous vos besoins. Spécialistes du dépannage urgence, de la rénovation énergétique et de l\'installation de sanitaires modernes (douches italiennes, WC suspendus).',
      delai: '15-20 minutes'
    },
    {
      name: 'Neuilly-sur-Seine (92200)',
      description: 'Neuilly-sur-Seine fait partie de notre zone d\'intervention, avec ses quartiers résidentiels de standing. Expertise dans les appartements haut de gamme et hôtels particuliers. Nous garantissons des interventions soignées et conformes aux normes. Devis gratuit pour tous travaux de plomberie.',
      delai: '20-25 minutes'
    }
  ];

  const breadcrumbItems = [
    { name: 'Zone d\'intervention', url: '/zone-intervention' }
  ];

  return (
    <>
      <SEO
        title="Zone d'intervention - Plombier Paris 17 et arrondissements | NHM"
        description="NHM Plomberie intervient à Paris 17e, 8e, 9e, 16e, 18e, Levallois, Clichy, Neuilly. Délais d'intervention 15-25 min. ☎ 09 70 70 58 63"
        keywords="plombier Paris 17, plombier Paris 8, plombier Paris 16, plombier Levallois, plombier Clichy, plombier Neuilly, zone intervention plomberie Paris"
        canonical="https://nhm-plomberie.fr/zone-intervention"
      />
      <BreadcrumbSchema items={[{ name: 'Accueil', url: 'https://nhm-plomberie.fr/' }, ...breadcrumbItems.map(item => ({
        name: item.name,
        url: `https://nhm-plomberie.fr${item.url}`
      }))]} />

      <Breadcrumb items={breadcrumbItems} />

      {/* Hero Section */}
      <section className="bg-blue-900 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Notre Zone d'Intervention en Plomberie</h1>
          <p className="text-xl text-blue-100 max-w-4xl">
            NHM Plomberie couvre Paris 17e et l'ensemble des arrondissements et communes environnantes. 
            Nos plombiers professionnels interviennent rapidement, 24h/24 et 7j/7, avec des délais de 15 à 25 minutes 
            selon votre localisation. Découvrez ci-dessous tous les secteurs où nous sommes présents.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Un Service de Proximité à Paris et Alentours</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Basée à <strong>Paris 17e (75017)</strong>, au 18 rue Legendre, notre entreprise 
                de plomberie NHM Plomberie a développé une expertise locale incomparable dans la capitale 
                et sa proche banlieue. Cette implantation stratégique nous permet d'offrir des temps d'intervention 
                exceptionnellement rapides, essentiels lorsqu'une urgence plomberie survient.
              </p>
              <p>
                Chaque jour, nos équipes de <strong>plombiers qualifiés et assurés</strong> sillonnent les arrondissements 
                et communes limitrophes pour répondre à vos besoins : dépannage d'urgence, réparation de fuite, débouchage 
                de canalisation, installation sanitaire, remplacement de chauffe-eau, rénovation complète... 
                Quel que soit votre projet, nous nous déplaçons chez vous avec tout le matériel professionnel 
                nécessaire.
              </p>
              <p>
                Notre connaissance approfondie du territoire parisien (réseau d'assainissement, immeubles haussmanniens, 
                spécificités des copropriétés) fait de nous le partenaire idéal pour tous vos travaux de plomberie, 
                petits ou grands. Nous intervenons aussi bien chez les particuliers que pour les professionnels, 
                syndics de copropriété et entreprises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Zones List */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Secteurs et Délais d'Intervention</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {zones.map((zone, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                      <span className="text-xl">{zone.name}</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">{zone.description}</p>
                  <div className="flex items-center gap-2 text-blue-600 font-semibold">
                    <Clock size={18} />
                    <span>Intervention en {zone.delai}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <MapPin className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Votre Secteur N'Est Pas Listé ?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Contactez-nous pour vérifier si nous intervenons dans votre quartier. 
            Nous étendons régulièrement notre zone de couverture.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 py-6">
              <a href="tel:0970705863" className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                09 70 70 58 63
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6">
              <Link to="/contact">Devis Gratuit</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default ZoneIntervention;
